import React, { useEffect, useState } from 'react';
import { Input, Spin } from 'antd';
import moment from 'moment/moment.js';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import Segmented from '../../../components/segmented/Segmented.jsx';
import Pagination from '../../../components/pagination/Pagination.jsx';
import ModalComponent from '../../../components/modals/ModalComponent.jsx';
import TableWithSkeleton from '../../../components/table/TableWithSkeleton.jsx';
import columns from '../../../columns/ib/IBTeam.js';
import Eye from '../../../assets/Eye.jsx';
import { GetIBTeamApi, GetUserDetailsByIdApi } from '../../../api/request/ib/ibTeam.js';

export default function IBTeamMobile() {
  const { userDetails } = useSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sorter, setSorter] = useState({ SortBy: 'createdDate', SortOrder: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [valuenew, setValuenew] = useState(searchParams?.get('filter')?.trim() || 'All Team');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [trader, setTrader] = useState({});
  const [isUserLoading, setIsUserLoading] = useState(false);

  useEffect(() => {
    setSearchParams(`filter=${valuenew}`);
  }, [valuenew]);

  const getIBTeam = async () => {
    try {
      setIsLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        SearchText: searchText,
        FilterText: userDetails?.referralCode || ' ',
        ...sorter,
      };
      const { data } = await GetIBTeamApi(new URLSearchParams(params).toString());
      if (data?.success) {
        setData(
          (data?.data || []).map((item) => ({
            ...item,
            name: `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
            createdDate: item?.createdDate ? moment(item.createdDate).format('DD-MM-YYYY LT') : null,
            actions: (
              <div
                className="cursor-pointer"
                onClick={() => {
                  setSelectedUserId(item?.userId);
                  setIsOpenModal(true);
                }}
              >
                <Eye />
              </div>
            ),
          }))
        );
        setTotalRecord(data?.totalRecords || 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
    } catch (error) {
      setData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserDetails = async () => {
    try {
      setIsUserLoading(true);
      const { data } = await GetUserDetailsByIdApi(selectedUserId);
      if (data?.success) {
        setTrader(data?.data);
      }
    } catch (error) {
    } finally {
      setIsUserLoading(false);
    }
  };

  useEffect(() => {
    getIBTeam();
  }, [page, pageSize, searchText, sorter, valuenew]);

  useEffect(() => {
    if (selectedUserId) getUserDetails();
  }, [selectedUserId]);

  useEffect(() => {
    if (!isOpenModal) {
      setSelectedUserId('');
      setTrader({});
    }
  }, [isOpenModal]);

  const handleTableChange = (pagination, filters, tableSorter) => {
    if (tableSorter?.order) {
      setSorter({ SortBy: tableSorter?.field, SortOrder: tableSorter?.order });
    } else {
      setSorter({ SortBy: 'createdDate', SortOrder: '' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-3 shadow-sm space-y-3">
        <Segmented
          className="w-full"
          size="large"
          block
          options={['All Team', 'My Team']}
          value={valuenew}
          setValue={(value) => {
            setValuenew(value);
            setPage(1);
          }}
        />

        <Input
          size="large"
          placeholder="Search team member"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => {
            setSearchText(search);
            setPage(1);
          }}
          className="rounded-xl w-full"
          prefix={<SearchOutlined className="text-gray-400" />}
          suffix={
            search ? (
              <button
                type="button"
                className="text-gray-400 text-xs"
                onClick={() => {
                  setSearch('');
                  setSearchText('');
                  setPage(1);
                }}
              >
                Clear
              </button>
            ) : null
          }
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <TableWithSkeleton columns={columns} data={data} loading={isLoading} onChange={handleTableChange} />
        </div>
      </div>

      {totalRecord > 0 && (
        <Pagination total={totalRecord} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />
      )}

      <ModalComponent isOpen={isOpenModal} setIsOpen={setIsOpenModal} title="Trader Information" width="100%">
        {isUserLoading ? (
          <div className="flex justify-center flex-col my-10">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {[
              ['First Name', trader?.firstName],
              ['Last Name', trader?.lastName],
              ['Email', trader?.email],
              ['Mobile No.', trader?.phoneNo],
              ['Country', trader?.countryName],
              ['Affiliate Code', trader?.affiliateCode],
              ['Demo Accounts', trader?.demoAccount],
              ['Live Accounts', trader?.liveAccount],
              ['Live Plan', trader?.livePlan],
              ['Wallet No.', trader?.walletAccountNo],
              ['Wallet Balance', `$ ${trader?.walletBalance || 0}`],
              ['Created Date', trader?.createdDate ? moment(trader.createdDate).format('DD-MM-YYYY LT') : '--'],
            ].map(([label, value]) => (
              <div key={label} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-semibold primary mt-1 break-all">{value || '--'}</p>
              </div>
            ))}
          </div>
        )}
      </ModalComponent>
    </div>
  );
}
